import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
  Grid,
  Button,
} from "@mui/material";
import { CopyAll } from "@mui/icons-material";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import ButtonOne from "../../common/normal-button";
import typography from "../../cssUtils/typography";

// Phone libs (same as QR/UserFormDialog)
import { getCountryCallingCode } from "react-phone-number-input/input";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import parsePhoneNumber, { getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { ColorsConstants } from "../../cssUtils/constants";
import { useAuth } from "../../hooks/AuthProvider";
import { GENERATE_QR_GET_NUMBER_ENDPOINT } from "../../config/urls";
import CountryCodeSelect from "../../common/country-code-select";
import { getCountryForCallingCode as getCountryFromCallingCode } from "../../common/country-codes";

const placements = [
  { label: "Top Left", value: "top-left" },
  { label: "Top Right", value: "top-right" },
  { label: "Bottom Left", value: "bottom-left" },
  { label: "Bottom Right", value: "bottom-right" },
];

const WAChatWidget = () => {
  const { getValidToken } = useAuth();

  const [formState, setFormState] = useState({
    country_iso: "IN",
    phoneNumber: "",
    message: "Hi",
    cardTitle: "Chat with us!",
    iconPlacement: "bottom-right",
    buttonBgColor: "#1DAA61",
  });

  const [generatedScript, setGeneratedScript] = useState("");
  const [error, setError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [showAlert, setShowAlert] = useState(false);

  const getExpectedDigitCount = (iso) => {
    try {
      const example = getExampleNumber(iso, examples);
      return example?.nationalNumber?.length || null;
    } catch {
      return null;
    }
  };

  const validatePhone = (phone, iso) => {
    if (!phone) return "Please enter a phone number";

    const expected = getExpectedDigitCount(iso);
    if (expected && phone.length !== expected) {
      return `Enter a valid ${iso} phone number (${expected} digits)`;
    }

    const full = `+${getCountryCallingCode(iso)}${phone}`;
    if (!isPossiblePhoneNumber(full)) return "Invalid phone number";

    return "";
  };

  const validateMessage = (text) =>
    /^[a-zA-Z0-9 +.,!?':;"()/\-[\]]*$/.test(text);

 
  const handleGenerate = () => {
    const phoneErr = validatePhone(
      formState.phoneNumber,
      formState.country_iso,
    );
    if (phoneErr) {
      setError(phoneErr);
      return;
    }
    if (!formState.cardTitle.trim()) {
      setError("Card title required");
      return;
    }
    if (!formState.message.trim()) {
      setMessageError("Message is required");
      return;
    }
    if (formState.message.length > 30) {
      setMessageError("Message cannot exceed 30 characters");
      return;
    }
    setError("");
    setMessageError("");

    const dial = getCountryCallingCode(formState.country_iso);
    const script = `
        <script>
          (function createWhatsAppWidget() {
            const phoneNumber = '${dial}${formState.phoneNumber}';
            const defaultMessage = '${formState.message.trim()}';
            const cardTitle = '${formState.cardTitle.trim()}';
            const iconPlacement = '${formState.iconPlacement}';
            const buttonBgColor = '${formState.buttonBgColor}';

            // Your Widget Code Here...
            const styles = \`
               /* Add your CSS styles here */
               body {
                  font-family: 'Roboto', sans-serif;
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                }
                #whatsapp-widget-container {
                  position: fixed;
                  bottom: 20px;
                  ${formState.iconPlacement === 'bottom-left' ? 'left: 20px;' : 'right: 20px;'}
                  z-index: 9999;
                }
                #whatsapp-widget {
                  width: 60px;
                  height: 60px;
                  background-color: \${buttonBgColor};
                  border-radius: 50%;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
                  cursor: pointer;
                  transition: transform 0.3s ease;
                }
                #whatsapp-widget:hover {
                  transform: scale(1.1);
                }
                #whatsapp-widget img {
                  width: 35px;
                  height: 35px;
                }
                #whatsapp-popup {
                  position: absolute;
                  bottom: 80px;
                  ${formState.iconPlacement === 'bottom-left' ? 'left: 0;' : 'right: 0;'}
                  background-color: #ffffff;
                  width: 320px;
                  border-radius: 8px;
                  box-shadow: 0px 6px 15px rgba(0, 0, 0, 0.2);
                  display: none;
                  flex-direction: column;
                  overflow: hidden;
                }
                #whatsapp-header {
                  background-color: \${buttonBgColor};
                  color: #fff;
                  padding: 15px;
                  text-align: center;
                  font-size: 18px;
                  font-weight: 500;
                  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
                }
                #whatsapp-body {
                  padding: 20px;
                }
                #whatsapp-body label {
                  font-size: 14px;
                  color: #333;
                  display: block;
                  margin-bottom: 8px;
                }
                #whatsapp-body input {
                  width: calc(100% - 30px);
                  padding: 10px;
                  margin-bottom: 15px;
                  border: 1px solid #ddd;
                  border-radius: 5px;
                  font-size: 16px;
                }
                #whatsapp-body button {
                  width: calc(100% - 10px);
                  background-color: \${buttonBgColor};
                  color: white;
                  padding: 12px;
                  border: none;
                  border-radius: 5px;
                  font-size: 16px;
                  font-weight: 500;
                  cursor: pointer;
                  transition: background-color 0.3s ease;
                }
               #whatsapp-body button:hover {
                 background-color: #1da851;
               }
             \`;

             const styleSheet = document.createElement('style');
             styleSheet.type = 'text/css';
             styleSheet.innerText = styles;
             document.head.appendChild(styleSheet);

             const container = document.createElement('div');
             container.id = 'whatsapp-widget-container';

             const widget = document.createElement('div');
             widget.id = 'whatsapp-widget';
             widget.innerHTML = \`
               <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp">
             \`;

             const popup = document.createElement('div');
             popup.id = 'whatsapp-popup';
             popup.innerHTML = \`
               <div id="whatsapp-header">\${cardTitle}</div>
               <div id="whatsapp-body">
                 <label for="message">Message</label>
                 <input type="text" id="message" placeholder="Please Type a message" value="\${defaultMessage}" />
                 <button id="send-btn">Send</button>
               </div>
             \`;

             container.appendChild(widget);
             container.appendChild(popup);

             document.body.appendChild(container);

             widget.addEventListener('click', () => {
               popup.style.display = popup.style.display === 'flex' ? 'none' : 'flex';
             });

             document.getElementById('send-btn').addEventListener('click', () => {
               const userMessage = document.getElementById('message').value.trim();
               if (userMessage) {
                 const url = \`https://api.whatsapp.com/send/?phone=\${phoneNumber}&text=\${encodeURIComponent(userMessage)}\`;
                 window.open(url, '_blank');
               } else {
                 alert('Please type a message before sending!');
               }
             });
          })();
        </script>
      `;

    setGeneratedScript(script.trim());
    setShowAlert(true);
  };

  useEffect(() => {
    if (showAlert) {
      const t = setTimeout(() => setShowAlert(false), 4000);
      return () => clearTimeout(t);
    }
  }, [showAlert]);

  useEffect(() => {
    const fetchBusinessNumber = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const response = await fetch(GENERATE_QR_GET_NUMBER_ENDPOINT, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenant,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const rawFullNumber = data.business_wa_number;

          if (rawFullNumber) {
            const formatted = rawFullNumber.startsWith("+")
              ? rawFullNumber
              : `+${rawFullNumber}`;
            const parsed = parsePhoneNumber(formatted);

            if (parsed) {
              setFormState((prev) => ({
                ...prev,
                country_iso: parsed.country || getCountryFromCallingCode(parsed.countryCallingCode) || "IN",
                phoneNumber: parsed.nationalNumber,
                message: "Hi",
              }));
            } else {
              setFormState((prev) => ({
                ...prev,
                phoneNumber: rawFullNumber.replace(/\D/g, ""),
                message: "Hi",
              }));
            }
          }
        }
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };
    fetchBusinessNumber();
  }, [getValidToken]);

  return (
    <Card sx={{ maxWidth: 1100, margin: "auto", mt: 2, p: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2.5 }}>
          Generate WhatsApp Widget Script
        </Typography>

        <Grid container spacing={2}>
          {/* Country */}
          <Grid item xs={4}>
            <Typography
              sx={{
                ...typography.bodySmall,
                color: "#6C7C93",
                mb: 0.5,
                ml: 0.5,
              }}
            >
              Country Code*
            </Typography>
            <CountryCodeSelect
              value={formState.country_iso}
              onChange={(nextIso) => {
                setFormState({ ...formState, country_iso: nextIso });
                setError(
                  validatePhone(formState.phoneNumber, nextIso),
                );
              }}
              label=""
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={8}>
            <Typography
              sx={{
                ...typography.bodySmall,
                color: "#6C7C93",
                mb: 0.5,
                ml: 0.5,
              }}
            >
              Phone number*
            </Typography>
            <TextField
              fullWidth
              placeholder="9999999999"
              value={formState.phoneNumber}
              onChange={(e) => {
                const expected = getExpectedDigitCount(formState.country_iso);
                const val = e.target.value.replace(/\D/g, "");
                if (expected && val.length > expected) {
                  return;
                }
                setFormState({ ...formState, phoneNumber: val });
                setError(validatePhone(val, formState.country_iso));
              }}
              error={!!error}
              helperText={error}
            />
          </Grid>

          {/* Card Title */}
          <Grid item xs={12}>
            <Typography
              sx={{
                ...typography.bodySmall,
                color: "#6C7C93",
                mb: 0.5,
                ml: 0.5,
              }}
            >
              Card Title*
            </Typography>
            <TextField
              fullWidth
              value={formState.cardTitle}
              onChange={(e) =>
                setFormState({ ...formState, cardTitle: e.target.value })
              }
              inputProps={{ maxLength: 25 }}
              helperText={`${formState.cardTitle.length}/25`}
            />
          </Grid>

          {/* Message */}
          <Grid item xs={12}>
            <Typography
              sx={{
                ...typography.bodySmall,
                color: "#6C7C93",
                mb: 0.5,
                ml: 0.5,
              }}
            >
              Pre-filled message
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={formState.message}
              onChange={(e) => {
                const val = e.target.value;
                if (!validateMessage(val)) {
                  setMessageError("Invalid characters");
                } else {
                  setMessageError("");
                  setFormState({ ...formState, message: val });
                }
              }}
              inputProps={{ maxLength: 30 }}
              helperText={messageError || `${formState.message.length}/30`}
              error={!!messageError}
            />
          </Grid>

          {/* Icon Placement */}
          <Grid item xs={12}>
            <Typography sx={{ ...typography.bodySmall, mb: 1 }}>
              Icon Placement
            </Typography>

            <Grid container spacing={1}>
              {placements.map((p) => {
                const isSelected = formState.iconPlacement === p.value;

                return (
                  <Grid item xs={6} key={p.value}>
                    <Button
                      fullWidth
                      onClick={() =>
                        setFormState({ ...formState, iconPlacement: p.value })
                      }
                      sx={{
                        ...typography.bodyLarge,
                        borderRadius: 2,
                        height: 38,
                        textTransform: "none",
                        fontWeight: 600,

                        // ✅ selected state
                        backgroundColor: isSelected
                          ? ColorsConstants.primaryBlue100
                          : "transparent",

                        color: isSelected
                          ? "#fff"
                          : ColorsConstants.primaryBlue100,

                        border: `1px solid ${ColorsConstants.primaryBlue100}`,

                        "&:hover": {
                          backgroundColor: isSelected
                            ? ColorsConstants.primaryBlue100
                            : ColorsConstants.primaryPurple60,
                        },
                      }}
                    >
                      {p.label}
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>

          {/* Color Picker */}
          <Grid item xs={12}>
            <Typography sx={{ ...typography.bodySmall, mb: 1 }}>
              Button Background Color
            </Typography>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <input
                type="color"
                value={formState.buttonBgColor}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    buttonBgColor: e.target.value,
                  })
                }
                style={{
                  width: 50,
                  height: 40,
                  border: "none",
                  cursor: "pointer",
                }}
              />

              <TextField
                value={formState.buttonBgColor}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    buttonBgColor: e.target.value,
                  })
                }
              />
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <ButtonOne onClick={handleGenerate}>Generate Script</ButtonOne>
        </Box>

        {showAlert && (
          <Alert sx={{ mt: 2 }} severity="success">
            Script generated successfully!
          </Alert>
        )}

        {generatedScript && (
          <Box sx={{ mt: 2, position: "relative" }}>
            <SyntaxHighlighter language="javascript" style={oneLight}>
              {generatedScript}
            </SyntaxHighlighter>

            <IconButton
              onClick={() => navigator.clipboard.writeText(generatedScript)}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                color: "white",
                backgroundColor: "#25d366",
              }}
            >
              <CopyAll />
            </IconButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default WAChatWidget;
